import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    Circle as CircleIcon,
} from '@mui/icons-material';
import timesheetService from '../../../services/timesheetService';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

export default function CalendarPickerModal({
    open,
    onClose,
    currentDate,
    maxNavigableDate,
    calendarData,
    calendarLoading,
    onSelectDate,
    onMonthChange,
}) {
    const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
    const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());

    useEffect(() => {
        if (open) {
            const d = new Date(currentDate + 'T00:00:00');
            setViewYear(d.getFullYear());
            setViewMonth(d.getMonth());
        }
    }, [open, currentDate]);

    const navigateMonth = useCallback((direction) => {
        let newMonth = viewMonth + direction;
        let newYear = viewYear;
        if (newMonth < 0) { newMonth = 11; newYear--; }
        if (newMonth > 11) { newMonth = 0; newYear++; }
        setViewMonth(newMonth);
        setViewYear(newYear);
        onMonthChange(newYear, newMonth);
    }, [viewMonth, viewYear, onMonthChange]);

    // Build calendar grid
    const buildCalendarGrid = () => {
        const firstDay = new Date(viewYear, viewMonth, 1);
        const lastDay = new Date(viewYear, viewMonth + 1, 0);
        let startDow = firstDay.getDay(); // 0=Sun
        startDow = startDow === 0 ? 6 : startDow - 1; // Convert to Mon=0

        const days = [];
        // Previous month padding
        for (let i = 0; i < startDow; i++) {
            const d = new Date(viewYear, viewMonth, -startDow + i + 1);
            days.push({ date: timesheetService.toLocalDateString(d), isCurrentMonth: false });
        }
        // Current month days
        for (let i = 1; i <= lastDay.getDate(); i++) {
            const d = new Date(viewYear, viewMonth, i);
            days.push({ date: timesheetService.toLocalDateString(d), isCurrentMonth: true });
        }
        // Next month padding
        const remaining = 42 - days.length; // 6 rows x 7 cols
        for (let i = 1; i <= remaining; i++) {
            const d = new Date(viewYear, viewMonth + 1, i);
            days.push({ date: timesheetService.toLocalDateString(d), isCurrentMonth: false });
        }

        // Split into weeks
        const weeks = [];
        for (let i = 0; i < days.length; i += 7) {
            weeks.push(days.slice(i, i + 7));
        }
        return weeks;
    };

    const today = timesheetService.toLocalDateString();
    const weeks = buildCalendarGrid();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <IconButton onClick={() => navigateMonth(-1)} size="small">
                        <ChevronLeft />
                    </IconButton>
                    <Typography variant="h6" fontWeight={600}>
                        {MONTH_NAMES[viewMonth]} {viewYear}
                    </Typography>
                    <IconButton onClick={() => navigateMonth(1)} size="small">
                        <ChevronRight />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {/* Day names header */}
                <Box sx={{ display: 'flex', mb: 0.5 }}>
                    {DAY_NAMES.map(d => (
                        <Box
                            key={d}
                            sx={{
                                flex: 1,
                                textAlign: 'center',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                color: 'text.secondary',
                                py: 0.5,
                            }}
                        >
                            {d}
                        </Box>
                    ))}
                </Box>

                {/* Calendar grid */}
                {weeks.map((week, weekIdx) => (
                    <Box key={weekIdx} sx={{ display: 'flex' }}>
                        {week.map((day) => {
                            const info = calendarData?.[day.date];
                            const isToday = day.date === today;
                            const isSelected = day.date === currentDate;
                            const isFuture = day.date > maxNavigableDate;
                            const dayNum = new Date(day.date + 'T00:00:00').getDate();

                            return (
                                <Box
                                    key={day.date}
                                    component="button"
                                    onClick={() => {
                                        if (!isFuture) onSelectDate(day.date);
                                    }}
                                    disabled={isFuture}
                                    sx={{
                                        flex: 1,
                                        aspectRatio: '1',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: 'none',
                                        borderRadius: 1,
                                        cursor: isFuture ? 'not-allowed' : 'pointer',
                                        bgcolor: isSelected
                                            ? 'primary.main'
                                            : isToday
                                                ? 'primary.50'
                                                : info?.weekStatus === 'submitted'
                                                    ? 'success.50'
                                                    : info?.weekStatus === 'signed'
                                                        ? 'info.50'
                                                        : 'transparent',
                                        color: isSelected
                                            ? 'white'
                                            : !day.isCurrentMonth || isFuture
                                                ? 'text.disabled'
                                                : 'text.primary',
                                        fontSize: '0.8rem',
                                        fontWeight: isToday || isSelected ? 700 : 400,
                                        p: 0.25,
                                        minHeight: 36,
                                        '&:hover': {
                                            bgcolor: isFuture
                                                ? 'transparent'
                                                : isSelected
                                                    ? 'primary.dark'
                                                    : 'action.hover',
                                        },
                                    }}
                                >
                                    {dayNum}
                                    {info?.hasRecords && !isSelected && (
                                        <CircleIcon sx={{
                                            fontSize: 5,
                                            color: isSelected ? 'white' : 'primary.main',
                                            mt: -0.25,
                                        }} />
                                    )}
                                    {info?.totalHours > 0 && (
                                        <Typography
                                            sx={{
                                                fontSize: '0.55rem',
                                                color: isSelected ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                                                lineHeight: 1,
                                            }}
                                        >
                                            {info.totalHours}h
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                ))}
            </DialogContent>
            {/* Legend */}
            <Box sx={{ px: 3, py: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.65rem', color: 'text.secondary' }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid', borderColor: 'primary.main' }} />
                    Today
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.65rem', color: 'text.secondary' }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: 'primary.main' }} />
                    Selected
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.65rem', color: 'text.secondary' }}>
                    <CircleIcon sx={{ fontSize: 6, color: 'primary.main' }} />
                    Has data
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.65rem', color: 'text.secondary' }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.main' }} />
                    Submitted
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.65rem', color: 'text.secondary' }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.main' }} />
                    Signed
                </Box>
            </Box>

            <DialogActions>
                <Button
                    onClick={() => onSelectDate(today)}
                    variant="text"
                    size="small"
                >
                    Today
                </Button>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
